/**
 * Data formatters can be provided to inputs to display a value
 * in a different format from the value that is stored. They should expose
 * one or both of the APIs:
 * - format(value)
 * - unformat(value)
 */

import { isFiniteNumber, convertToFiniteNumber } from "Util/util";

function removeThousandSeparators(numStr) {
    // Ignore null or empty
    if (!numStr) {
        return numStr;
    }

    // If the parameter is not a string, just return it
    if (!numStr.replace) {
        return numStr;
    } else {
        // remove comma separators from str
        return numStr.replace(/,/g, "");
    }
}

function addThousandSeparators(numStr) {
    // Ignore null or empty strings
    if (!numStr) {
        return numStr;
    }

    // If input has "replace" undefined it is not a string, so convert it to one
    if (!numStr.replace) {
        numStr = "" + numStr;
    }

    numStr = removeThousandSeparators(numStr).trim();

    // If input can't be parsed as finite number (eg. "Infinity"),
    // don't add separators
    if (!isFiniteNumber(convertToFiniteNumber(numStr))) {
        return numStr;
    }

    const isNegative = numStr.charAt(0) === "-";
    numStr = isNegative ? numStr.substring(1) : numStr;
    const parts = numStr.split(".");
    const sign = isNegative ? "-" : "";
    const intPart = parts[0];
    const decPart = parts[1] != null ? "." + parts[1] : "";

    const segments = [];
    let i = intPart.length;
    while (i >= 0) {
        const segmentEnd = i;
        i -= 3;
        const segmentStart = Math.max(0, i);
        const segment = intPart.substring(segmentStart, segmentEnd);

        if (segment) {
            segments.push(segment);
        }
    }

    return sign + segments.reverse().join(",") + decPart;
}

// Remove leading 0s from integers
function integerFormatter(numStr) {
    // Ignore empty input, only accept numeric input
    if (!numStr || Number.isNaN(numStr)) {
        return numStr;
    }

    if (typeof BigInt === "function") {
        try {
            // eslint-disable-next-line no-undef
            return BigInt(numStr.trim()).toString();
        } catch {
            return numStr;
        }
    }
    return numStr;
}

export const THOUSAND_SEPARATOR_FORMATTER = {
    format: addThousandSeparators,
    unformat: removeThousandSeparators
};

export const INTEGER_FORMATTER = {
    format: integerFormatter
};

export function getDataFormatter(formatters) {
    if (Array.isArray(formatters)) {
        return {
            format(value) {
                let result = value;
                // format in order
                for (let i = 0; i < formatters.length; i++) {
                    const formatter = formatters[i];
                    if (formatter.format) {
                        result = formatter.format(result);
                    }
                }
                return result;
            },
            unformat(value) {
                let result = value;
                // unformat in reverse order
                for (let i = formatters.length - 1; i >= 0; i--) {
                    const formatter = formatters[i];
                    if (formatter.unformat) {
                        result = formatter.unformat(result);
                    }
                }
                return result;
            }
        };
    } else {
        return formatters;
    }
}
